import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from './AuthLayout';
import { OTPInput } from './components/OTPInput';
import { otpSchema, OTPFormData } from './utils/validation';
import { Button } from '../../components/ui/button';
import { authApi } from '../../services/auth.api';
import { useAuthStore } from '../../store/authStore';
import { ApiError } from '../../services/api';
import { navigateAfterAuth } from '../../utils/authNavigation';

export function VerifyOTP() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('auth');
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const setAuth = useAuthStore((s) => s.setAuth);

  const {
    emailOrPhone,
    inputType,
    isNewUser,
    otpType,
    redirectTo,
  } = (location.state || {}) as {
    emailOrPhone?: string;
    inputType?: string;
    isNewUser?: boolean;
    otpType?: 'EMAIL_VERIFY' | 'LOGIN_OTP';
    redirectTo?: string;
  };

  const resolvedType = inputType || 'email';
  const resolvedOtpType = otpType || (isNewUser ? 'EMAIL_VERIFY' : 'LOGIN_OTP');

  const {
    watch,
    setValue,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<OTPFormData>({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      code: '',
    },
  });

  const code = watch('code');

  // Redirect if no email/phone provided
  useEffect(() => {
    if (!emailOrPhone) {
      navigate('/auth/login');
    }
  }, [emailOrPhone, navigate]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Start cooldown on mount
  useEffect(() => {
    setResendCooldown(30);
  }, []);

  const onSubmit = async (data: OTPFormData) => {
    setIsLoading(true);
    clearErrors();

    try {
      const result = await authApi.verifyOTP({
        email: emailOrPhone!,
        otp: data.code,
        type: resolvedOtpType,
      });

      setAuth(result.user, result.accessToken, result.refreshToken);
      navigateAfterAuth({ user: result.user, isNewUser, navigate, redirectTo });
    } catch (error) {
      if (error instanceof ApiError) {
        setError('code', { type: 'manual', message: error.message });
      } else {
        setError('code', { type: 'manual', message: t('errors.genericError') });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setResendMessage(null);

    try {
      const result = await authApi.resendOTP({
        email: emailOrPhone!,
        type: resolvedOtpType,
      });

      if (result.success) {
        setResendCooldown(30);
        clearErrors();
        setResendMessage('New code sent!');
        setTimeout(() => setResendMessage(null), 3000);
      } else {
        setResendCooldown((result as { waitSeconds?: number }).waitSeconds || 30);
        setResendMessage(result.message);
        setTimeout(() => setResendMessage(null), 4000);
      }
    } catch (error) {
      setResendMessage(error instanceof ApiError ? error.message : t('errors.resendFailed'));
      setTimeout(() => setResendMessage(null), 4000);
    }
  };

  const handleCodeComplete = () => {
    handleSubmit(onSubmit)();
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Mask email/phone for display
  const displayContact = emailOrPhone
    ? resolvedType === 'email'
      ? emailOrPhone.replace(/(.{2})(.*)(@.*)/, '$1***$3')
      : emailOrPhone.replace(/(\+?\d{2})(\d+)(\d{2})$/, '$1***$3')
    : '';

  return (
    <AuthLayout>
      <div className="space-y-6 animate-fade-in-up stagger-1">
        {/* Header */}
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-normal text-slate-900 dark:text-zinc-100 tracking-tight">{t('otp.title')}</h2>
          <p className="text-sm text-slate-500 dark:text-zinc-400">
            {t('otp.sentTo')}{' '}
            <span className="font-medium text-slate-700 dark:text-zinc-200">{displayContact}</span>
          </p>
          {/* Change contact link */}
          <button
            type="button"
            onClick={handleBack}
            className="text-xs text-slate-400 dark:text-zinc-500 hover:text-slate-700 dark:hover:text-zinc-200 underline underline-offset-2 transition-colors"
          >
            {resolvedType === 'phone' ? t('otp.wrongPhone') : t('otp.wrongEmail')}
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* OTP Input */}
          <OTPInput
            value={code}
            onChange={(value) => {
              setValue('code', value);
              clearErrors();
            }}
            onComplete={handleCodeComplete}
            error={errors.code?.message}
            disabled={isLoading}
          />

          {/* Verify Button */}
          <Button
            type="submit"
            className="w-full h-12 text-sm font-normal bg-slate-900 hover:bg-slate-800 text-white rounded-xl transition-all duration-200 shadow-sm hover:shadow-md active:scale-[0.98]"
            disabled={isLoading || code.length !== 6}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                {t('otp.verifying')}
              </span>
            ) : (
              t('otp.verify')
            )}
          </Button>

          {/* Resend */}
          <div className="text-center space-y-1">
            {resendMessage && (
              <p className="text-xs text-emerald-600 animate-fade-in">{resendMessage}</p>
            )}
            <button
              type="button"
              onClick={handleResendCode}
              disabled={resendCooldown > 0}
              className="text-sm text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-zinc-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {resendCooldown > 0 ? (
                <span>{t('otp.resendIn', { seconds: resendCooldown })}</span>
              ) : (
                <span className="underline underline-offset-2">{t('otp.resendCode')}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
}
